import {
  Box,
  BoxProps,
  Divider,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiTag, FiUser } from "react-icons/fi";
import { IoPricetagsOutline } from "react-icons/io5";
import { CreateSegmentFormValues as FormValues } from ".";
import { KYDNumericOperator } from "common/interfaces/KYDCampaign";
import { genderToText } from "common/services/util/campaigns";

const Card = ({
  title,
  children,
  icon,
  ...props
}: {
  title: string;
  children: React.ReactNode;
  icon: React.ReactNode;
} & BoxProps) => {
  return (
    <Box
      display="flex"
      w="100%"
      padding="16px 0px"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="flex-start"
      flexShrink={0}
      borderRadius="10px"
      border="1px solid #E6E6E6"
      background="#FFF"
      boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.05)"
      py={4}
      px={3}
      {...props}
    >
      <HStack
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        mb={6}
      >
        <Text
          color="rgba(34, 34, 34, 0.80)"
          fontSize="16px"
          fontWeight={500}
          lineHeight="23px"
          letterSpacing="-0.48px"
        >
          {title}
        </Text>
        {icon}
      </HStack>
      {children}
    </Box>
  );
};

const ReviewCreateSegment = ({
  data,
  segmentSize,
}: {
  data: FormValues;
  segmentSize: number;
}) => {
  const renderNumericValue = (
    title: string,
    value: string,
    operator?: KYDNumericOperator
  ) => {
    return (
      <VStack align="flex-start">
        <Text color="#718096" fontSize="14px" fontWeight={500}>
          {title}
        </Text>
        <Text color="#1A202C" fontSize="20px">
          {operator} {value}
        </Text>
      </VStack>
    );
  };

  const renderFilterSummary = (
    title: string,
    count: string,
    values: string[]
  ) => {
    return (
      <VStack gap={2.5}>
        <HStack w="100%">
          <Text color="#718096" fontSize="14px" fontWeight={500}>
            {title}
          </Text>
          <Box
            bg="#666"
            borderRadius="20px"
            px={1.5}
            py={0.75}
            color="white"
            fontSize="12px"
          >
            {count}
          </Box>
        </HStack>
        <HStack gap={2.5} w="100%" flexWrap="wrap">
          {values.map((value) => (
            <Box
              key={value}
              bg="#EDF2F7"
              borderRadius="4px"
              px={1.5}
              color="rgba(0, 0, 0, 0.70)"
              fontWeight={500}
              fontSize="14px"
              lineHeight="23px"
            >
              {value}
            </Box>
          ))}
        </HStack>
      </VStack>
    );
  };

  return (
    <VStack align="flex-start">
      <Divider borderBottom="1px solid rgba(0, 0, 0, 0.20)" mb={6} />
      <Text
        color="rgba(34, 34, 34, 0.80)"
        fontSize="16px"
        fontWeight={500}
        lineHeight="23px"
        letterSpacing="-0.48px"
      >
        Segment Name:
      </Text>
      <Text
        color="#1A202C"
        fontSize="20px"
        fontWeight={400}
        lineHeight="120%"
        letterSpacing="-0.6px"
      >
        {data.name}
      </Text>
      <HStack mt={4}>
        <Box borderRight="1px solid rgba(0, 0, 0, 0.20)" pr={4}>
          <VStack align="flex-start">
            <Text
              color="rgba(34, 34, 34, 0.80)"
              fontSize="16px"
              fontWeight={500}
              lineHeight="23px"
              letterSpacing="-0.48px"
            >
              Segment size:
            </Text>
            <Text
              color="#1A202C"
              fontSize="20px"
              fontWeight={400}
              lineHeight="120%"
              letterSpacing="-0.6px"
            >
              {Number(segmentSize).toLocaleString()} Contacts
            </Text>
          </VStack>
        </Box>
        <VStack align="flex-start">
          <Text
            color="rgba(34, 34, 34, 0.80)"
            fontSize="16px"
            fontWeight={500}
            lineHeight="23px"
            letterSpacing="-0.48px"
          >
            Auto Update
          </Text>
          <Text
            color="#1A202C"
            fontSize="20px"
            fontWeight={400}
            lineHeight="120%"
            letterSpacing="-0.6px"
          >
            {data.type === "dynamic" ? "On" : "Off"}
          </Text>
        </VStack>
      </HStack>

      <HStack gap={3} w="100%" mt={6} align="stretch">
        <Box w="50%">
          <Card title="Event Based" icon={<Icon as={FiTag} />}>
            <VStack gap={6} w="100%" align="stretch">
              {renderFilterSummary(
                "Genre",
                data.filters.genres.length.toString(),
                data.filters.genres.map((genre) => genre.label)
              )}

              {renderFilterSummary(
                "Event",
                data.filters.events.length.toString(),
                data.filters.events.map((event) => event.label)
              )}
              {/* TODO: Add event names */}

              {renderFilterSummary(
                "Venue",
                data.filters.venues.length.toString(),
                data.filters.venues.map((venue) => venue.label)
              )}
            </VStack>
          </Card>
        </Box>
        <HStack gap={3} flex={1}>
          <Card
            title="Demographics"
            icon={<Icon as={FiUser} />}
            flex={1}
            h="100%"
          >
            <HStack
              gap={6}
              w="100%"
              justifyContent="space-between"
              alignItems="flex-start"
              h="100%"
            >
              {renderNumericValue(
                "Age",
                data.filters.age.value.toString(),
                data.filters.age.operator
              )}
              {renderNumericValue("Gender", genderToText(data.filters.gender))}
            </HStack>
          </Card>
          <Card
            title="Tags"
            flex={1}
            icon={<Icon as={IoPricetagsOutline} />}
            h="100%"
          >
            {data.filters.custom_tags.length > 0 ? (
              <HStack
                gap={2}
                w="100%"
                alignItems="flex-start"
                h="100%"
                flexWrap="wrap"
                flexDir={"column"}
              >
                {data.filters.custom_tags.map((tag) => (
                  <Box
                    key={tag.value}
                    bg="#EDF2F7"
                    borderRadius="4px"
                    px={1.5}
                    color="rgba(0, 0, 0, 0.70)"
                    fontWeight={500}
                    fontSize="14px"
                    lineHeight="23px"
                  >
                    {tag.label}
                  </Box>
                ))}
              </HStack>
            ) : null}
          </Card>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default ReviewCreateSegment;
